// ============================================================================
// APPROVAL SERVICE - Manages content approval queue CRUD operations
// ============================================================================

import { supabase } from "@/integrations/supabase/client";

// ============================================================================
// TYPES
// ============================================================================

export interface ApprovalQueueItem {
  id: string;
  contentType: "show" | "episode";
  contentId: string;
  userId: string;
  status: "pending" | "pending_distribution" | "approved" | "delivered" | "rejected";
  assignedPlatforms: string[];
  reviewedBy: string | null;
  reviewedAt: string | null;
  rejectionReason: string | null;
  createdAt: string;
  updatedAt: string;
  // Joined fields
  contentTitle?: string;
  contentStatus?: string;
  podcasterName?: string;
  podcasterEmail?: string;
  reviewerName?: string;
  // Contract gating (episodes only) — true when the episode's show requires
  // a signed podcaster contract but doesn't have one yet. Global
  // `contracts_enabled` gating stays in the UI layer.
  showId?: string;
  contractPending?: boolean;
  // Manual RSS import lane (shows only) — set when the show was submitted with
  // an RSS feed while `rss_automation_enabled` was off, so an admin has to
  // import it by hand.
  isRssImport?: boolean;
  rssUrl?: string | null;
}

/**
 * Global `rss_automation_enabled` setting. Enabled unless explicitly 'false',
 * so a read failure can never stop the existing automation.
 */
export async function isRssAutomationEnabled(): Promise<boolean> {
  const { data, error } = await supabase
    .from("admin_settings")
    .select("value")
    .eq("key", "rss_automation_enabled")
    .maybeSingle();
  if (error) return true;
  return data?.value !== "false";
}

// ============================================================================
// RSS IMPORT LANE
// ============================================================================

export type RssImportStatus = "importing" | "imported" | "not_imported";


export interface RssImportShow {
  showId: string;
  title: string;
  showStatus: string;
  rssUrl: string;
  manualRequest: boolean;
  ownerId: string;
  ownerName: string | null;
  ownerEmail: string | null;
  createdAt: string;
  episodeCount: number;
  rssEpisodeCount: number;
  importStatus: RssImportStatus;
}

/** Derived from real data — never stored, so it can't drift. */
function deriveRssImportStatus(row: {
  import_pending: boolean;
  episode_count: number;
}): RssImportStatus {
  if (row.import_pending) return "importing";
  // Any episodes at all means the feed's content is already in PODGO — the
  // imported_from_rss flag postdates the earliest imports, so it can't be
  // used to tell "imported" apart from "has episodes".
  if (row.episode_count > 0) return "imported";
  return "not_imported";
}


/** Statuses that still need an admin to do something. */
export const ACTIONABLE_RSS_STATUSES: RssImportStatus[] = [
  "not_imported",
  "importing",
];

/**
 * Every show with an RSS feed on file, with its real import state rolled up
 * server-side (57k episodes can't be counted in the browser).
 */
export async function getRssImportShows(): Promise<RssImportShow[]> {
  const { data, error } = await supabase.rpc("get_rss_import_shows" as any);
  if (error) throw error;

  const rows = (data ?? []) as Array<{
    show_id: string;
    title: string;
    show_status: string;
    rss_url: string;
    rss_import_requested: boolean;
    import_pending: boolean;
    owner_id: string;
    owner_name: string | null;
    owner_email: string | null;
    created_at: string;
    episode_count: number;
    rss_episode_count: number;
  }>;

  return rows
    .map((r) => ({
      showId: r.show_id,
      title: r.title,
      showStatus: r.show_status,
      rssUrl: r.rss_url,
      manualRequest: r.rss_import_requested === true,
      ownerId: r.owner_id,
      ownerName: r.owner_name,
      ownerEmail: r.owner_email,
      createdAt: r.created_at,
      episodeCount: Number(r.episode_count ?? 0),
      rssEpisodeCount: Number(r.rss_episode_count ?? 0),
      importStatus: deriveRssImportStatus({
        import_pending: r.import_pending,
        episode_count: Number(r.episode_count ?? 0),
      }),
    }))
    .sort((a, b) => {
      // Manual submissions first, then actionable rows, then newest.
      if (a.manualRequest !== b.manualRequest) return a.manualRequest ? -1 : 1;
      const rank = (s: RssImportStatus) =>
        s === "not_imported" ? 0 : s === "importing" ? 1 : 2;

      const diff = rank(a.importStatus) - rank(b.importStatus);
      if (diff !== 0) return diff;
      return b.createdAt.localeCompare(a.createdAt);
    });
}




// ============================================================================
// HELPERS
// ============================================================================

/**
 * Fetch rows by a set of IDs without overflowing the request URL length limit.
 * PostgREST encodes `.in()` filters into the GET query string, so a few hundred
 * IDs produce a URL the server rejects (414) — which would silently return no
 * rows. Chunking keeps each request small and merges the results.
 */
async function fetchByIdsInChunks(
  table: "shows" | "episodes" | "profiles",
  columns: string,
  ids: string[],
  chunkSize = 80,
): Promise<any[]> {
  if (ids.length === 0) return [];
  const chunks: string[][] = [];
  for (let i = 0; i < ids.length; i += chunkSize) {
    chunks.push(ids.slice(i, i + chunkSize));
  }
  const results = await Promise.all(
    chunks.map((chunk) => supabase.from(table).select(columns).in("id", chunk)),
  );
  return results.flatMap((r) => r.data || []);
}

function mapRow(row: any): ApprovalQueueItem {
  return {
    id: row.id,
    contentType: row.content_type,
    contentId: row.content_id,
    userId: row.user_id,
    status: row.status,
    assignedPlatforms: row.assigned_platforms || [],
    reviewedBy: row.reviewed_by,
    reviewedAt: row.reviewed_at,
    rejectionReason: row.rejection_reason,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    contentTitle: row.content_title,
    podcasterName: row.podcaster_name,
    podcasterEmail: row.podcaster_email,
  };
}

// ============================================================================
// SERVICE FUNCTIONS
// ============================================================================

/**
 * Submit content to the approval queue (called when podcaster creates show/episode)
 */
export async function submitForApproval(
  contentType: "show" | "episode",
  contentId: string
): Promise<ApprovalQueueItem> {
  const { data: sessionData } = await supabase.auth.getSession();
  if (!sessionData.session) throw new Error("Not authenticated");

  const { data, error } = await supabase
    .from("content_approval_queue")
    .insert({
      content_type: contentType,
      content_id: contentId,
      user_id: sessionData.session.user.id,
      status: "pending",
    })
    .select()
    .single();

  if (error) throw error;

  // Update the content's own status to reflect pending approval
  if (contentType === "episode") {
    const { error: statusError } = await supabase
      .from("episodes")
      .update({ status: "pending_approval" })
      .eq("id", contentId);
    if (statusError) console.error("Failed to update episode status to pending_approval:", statusError);
  } else if (contentType === "show") {
    const { error: statusError } = await supabase
      .from("shows")
      .update({ status: "pending_approval" })
      .eq("id", contentId);
    if (statusError) console.error("Failed to update show status to pending_approval:", statusError);
  }

  return mapRow(data);
}

/**
 * Get all approval queue items (admin only)
 * Enriches with content title and podcaster name via separate queries
 */
export async function getApprovalQueue(
  statusFilter?: string
): Promise<ApprovalQueueItem[]> {
  let query = supabase
    .from("content_approval_queue")
    .select("*")
    .order("created_at", { ascending: false });

  if (statusFilter && statusFilter !== "all") {
    query = query.eq("status", statusFilter);
  }

  const { data, error } = await query;
  if (error) throw error;
  if (!data || data.length === 0) return [];

  // Collect content IDs by type for batch lookup
  const showIds = data.filter((r) => r.content_type === "show").map((r) => r.content_id);
  const episodeIds = data.filter((r) => r.content_type === "episode").map((r) => r.content_id);
  const userIds = [...new Set(data.map((r) => r.user_id))];

  // Fetch titles + statuses in parallel (single batch — drop the redundant duplicate fetches)
  const reviewerIds = [...new Set(data.filter((r) => r.reviewed_by).map((r) => r.reviewed_by as string))];

  const [showsData, episodesData, profilesData, reviewersData] = await Promise.all([
    fetchByIdsInChunks("shows", "id, title, status, rss_import_requested, rss_url", showIds),
    fetchByIdsInChunks("episodes", "id, title, status, show_id", episodeIds),
    fetchByIdsInChunks("profiles", "id, display_name", userIds),
    fetchByIdsInChunks("profiles", "id, display_name", reviewerIds),
  ]);

  const showsMap = new Map<
    string,
    { title: string; status: string; rssImport: boolean; rssUrl: string | null }
  >(
    showsData.map((s: any) => [
      s.id,
      {
        title: s.title,
        status: s.status,
        rssImport: s.rss_import_requested === true,
        rssUrl: s.rss_url ?? null,
      },
    ])
  );

  const episodesMap = new Map<string, { title: string; status: string; show_id: string }>(
    episodesData.map((e: any) => [e.id, { title: e.title, status: e.status, show_id: e.show_id }])
  );

  // Batch-fetch contract state for every show referenced by an episode row so
  // we can flag contract-blocked episodes without an N+1 lookup.
  const episodeShowIds = [
    ...new Set(episodesData.map((e: any) => e.show_id).filter(Boolean) as string[]),
  ];
  const episodeShowsContract = await fetchByIdsInChunks(
    "shows",
    "id, contract_required, contract_signed_at",
    episodeShowIds,
  );
  const contractMap = new Map<string, { required: boolean; signedAt: string | null }>(
    episodeShowsContract.map((s: any) => [
      s.id,
      { required: s.contract_required === true, signedAt: s.contract_signed_at ?? null },
    ]),
  );
  const profilesMap = new Map<string, string>(
    profilesData.map((p: any) => [p.id, p.display_name])
  );
  const reviewersMap = new Map<string, string>(
    reviewersData.map((p: any) => [p.id, p.display_name])
  );

  return data.map((row) => {
    const item = mapRow(row);
    if (row.content_type === "show") {
      const s = showsMap.get(row.content_id);
      item.contentTitle = s?.title || "Unknown Show";
      item.contentStatus = s?.status;
      item.showId = row.content_id;
      item.isRssImport = s?.rssImport === true;
      item.rssUrl = s?.rssUrl ?? null;
    } else {

      const e = episodesMap.get(row.content_id);
      item.contentTitle = e?.title || "Unknown Episode";
      item.contentStatus = e?.status;
      item.showId = e?.show_id;
      const c = e?.show_id ? contractMap.get(e.show_id) : undefined;
      item.contractPending = !!(c && c.required && !c.signedAt);
    }
    item.podcasterName = profilesMap.get(row.user_id) || "Unknown User";
    if (row.reviewed_by) {
      item.reviewerName = reviewersMap.get(row.reviewed_by) || "Unknown Admin";
    }
    return item;
  });
}

/**
 * Get pending count for badge display
 */
export async function getPendingCount(): Promise<number> {
  const { count, error } = await supabase
    .from("content_approval_queue")
    .select("*", { count: "exact", head: true })
    .eq("status", "pending");

  if (error) return 0;
  return count || 0;
}

/**
 * Count pending queue episodes that are blocked on an unsigned podcaster
 * contract. Mirrors the "Contract Pending" tab on /admin/approval-queue
 * exactly (same source rows, same `contractPending` rule) so the dashboard
 * tile and the tab can never disagree.
 */
export async function getContractPendingCount(): Promise<number> {
  const { data: queueRows, error: queueError } = await supabase
    .from("content_approval_queue")
    .select("content_id")
    .eq("status", "pending")
    .eq("content_type", "episode");

  if (queueError || !queueRows?.length) return 0;

  const episodeIds = [...new Set(queueRows.map((r) => r.content_id))];
  const { data: episodes, error: epError } = await supabase
    .from("episodes")
    .select("id, shows!inner(contract_required, contract_signed_at)")
    .in("id", episodeIds);

  if (epError || !episodes) return 0;

  return episodes.filter((e: any) => {
    const show = Array.isArray(e.shows) ? e.shows[0] : e.shows;
    return show && show.contract_required === true && !show.contract_signed_at;
  }).length;
}


/**
 * Get pending-distribution count (shows approved by network admins,
 * awaiting platform assignment by a platform admin).
 */
export async function getPendingDistributionCount(): Promise<number> {
  const { count, error } = await supabase
    .from("content_approval_queue")
    .select("*", { count: "exact", head: true })
    .eq("status", "pending_distribution");

  if (error) return 0;
  return count || 0;
}


/**
 * Approve a queue item with assigned platforms, then trigger automation resync
 */
export async function approveContent(
  queueId: string,
  assignedPlatforms: string[]
): Promise<void> {
  const { data: sessionData } = await supabase.auth.getSession();
  if (!sessionData.session) throw new Error("Not authenticated");

  // First, read the queue item to get content details
  const { data: queueItem, error: fetchError } = await supabase
    .from("content_approval_queue")
    .select("content_type, content_id, user_id")
    .eq("id", queueId)
    .single();

  if (fetchError || !queueItem) throw fetchError || new Error("Queue item not found");

  // For episodes, look up the parent show's hosting_platform automatically
  let platformsToAssign = assignedPlatforms;
  if (queueItem.content_type === "episode") {
    const { data: episode } = await supabase
      .from("episodes")
      .select("show_id, audio_url")
      .eq("id", queueItem.content_id)
      .single();

    if (!episode) throw new Error("Episode not found");

    const { data: show } = await supabase
      .from("shows")
      .select("hosting_platform" as any)
      .eq("id", episode.show_id)
      .single();

    const showData = show as any;
    if (!showData?.hosting_platform) {
      throw new Error("Parent show has not been assigned a hosting platform yet. Approve the show first.");
    }

    platformsToAssign = [showData.hosting_platform];

    if (showData.hosting_platform === "movie_night") {
      const mediaUrl = typeof episode.audio_url === "string" ? episode.audio_url.trim() : "";
      if (!mediaUrl || !/\.(mp4|mov|m4v|webm|mkv|mxf|mpeg|mpg|m3u8)(?:[?#].*)?$/i.test(mediaUrl)) {
        throw new Error("Movie Night episodes require a converted video file before approval. Re-convert the YouTube link or upload an MP4 first.");
      }
    }
  }

  // Mark as approved
  const { error } = await supabase
    .from("content_approval_queue")
    .update({
      status: "approved",
      assigned_platforms: platformsToAssign,
      reviewed_by: sessionData.session.user.id,
      reviewed_at: new Date().toISOString(),
    })
    .eq("id", queueId);

  if (error) throw error;

  // Activate the content now that it's approved — throw on failure to prevent silent inconsistency
  if (queueItem.content_type === "show") {
    const updatePayload: any = { status: "active" };
    if (assignedPlatforms.length > 0) {
      updatePayload.hosting_platform = assignedPlatforms[0];
    }
    const { error: activateError } = await supabase
      .from("shows")
      .update(updatePayload)
      .eq("id", queueItem.content_id);

    if (activateError) {
      console.error("Failed to activate show after approval:", activateError);
      throw new Error(`Show approved in queue but failed to activate: ${activateError.message}`);
    }
  } else if (queueItem.content_type === "episode") {
    const { error: publishError } = await supabase
      .from("episodes")
      .update({ status: "processing" })
      .eq("id", queueItem.content_id);

    if (publishError) {
      console.error("Failed to publish episode after approval:", publishError);
      throw new Error(`Episode approved in queue but failed to publish: ${publishError.message}`);
    }
  }

  // Enqueue the sync job for server-side background processing
  const resyncPath =
    queueItem.content_type === "show"
      ? `podgo-automation/podcasts/${queueItem.content_id}/resync`
      : `podgo-automation/episodes/${queueItem.content_id}/resync`;

  const { error: enqueueError } = await supabase
    .from("sync_queue")
    .insert({
      content_type: queueItem.content_type,
      content_id: queueItem.content_id,
      queue_id: queueId,
      assigned_platforms: platformsToAssign,
      status: "pending",
    });

  if (enqueueError) {
    console.error("Failed to enqueue sync job:", enqueueError);
    // Approval without a durable delivery job leaves content looking approved
    // even though no verified platform delivery can occur.
    throw new Error(`Content was approved but delivery could not be queued: ${enqueueError.message}`);
  }

  // If a show was just approved, handle its sibling `pending_approval` episodes:
  //   - Episodes that are already live on the show's hosting platform (they
  //     have an `episode_platform_ids` row for it, i.e. RSS-imported) are
  //     AUTO-APPROVED: marked published on PODGO and recorded in the queue as
  //     `approved` for audit — never enqueued into `sync_queue`, since
  //     re-delivering would duplicate them on the external platform.
  //   - Episodes without such a mapping (edge case: manually uploaded before
  //     the show got approved) keep the old behavior and get enqueued as
  //     `pending` for individual admin review.
  if (queueItem.content_type === "show") {
    try {
      const hostingPlatform = platformsToAssign[0];
      const { data: pendingEps } = await supabase
        .from("episodes")
        .select("id, user_id")
        .eq("show_id", queueItem.content_id)
        .eq("status", "pending_approval");

      const epIds = (pendingEps ?? []).map((e) => e.id);
      if (epIds.length > 0) {
        // Skip episodes already tracked in the approval queue.
        const { data: existingQueue } = await supabase
          .from("content_approval_queue")
          .select("content_id")
          .eq("content_type", "episode")
          .in("content_id", epIds);
        const alreadyQueued = new Set((existingQueue ?? []).map((r: any) => r.content_id));

        // Partition remaining episodes by whether they're already on the
        // hosting platform (RSS-imported → auto-approve) vs not (→ queue).
        // With RSS automation disabled every episode goes through manual
        // review, so the auto-approve branch is skipped entirely.
        const candidates = (pendingEps ?? []).filter((e) => !alreadyQueued.has(e.id));
        const rssAutomationOn = await isRssAutomationEnabled();
        let importedIds = new Set<string>();
        if (rssAutomationOn && hostingPlatform && candidates.length > 0) {
          const { data: mapped } = await supabase
            .from("episode_platform_ids")
            .select("episode_id")
            .eq("platform", hostingPlatform)
            .in("episode_id", candidates.map((e) => e.id));
          importedIds = new Set((mapped ?? []).map((r: any) => r.episode_id));
        }


        const toAutoApprove = candidates.filter((e) => importedIds.has(e.id));
        const toEnqueue = candidates.filter((e) => !importedIds.has(e.id));

        if (toAutoApprove.length > 0) {
          const nowIso = new Date().toISOString();
          const { error: publishErr } = await supabase
            .from("episodes")
            .update({ status: "published" })
            .in("id", toAutoApprove.map((e) => e.id));
          if (publishErr) console.error("Failed to publish imported episodes:", publishErr);

          const { error: autoQueueErr } = await supabase
            .from("content_approval_queue")
            .insert(
              toAutoApprove.map((e) => ({
                content_type: "episode" as const,
                content_id: e.id,
                user_id: e.user_id,
                status: "approved" as const,
                assigned_platforms: hostingPlatform ? [hostingPlatform] : [],
                reviewed_by: sessionData.session!.user.id,
                reviewed_at: nowIso,
              })),
            );
          if (autoQueueErr) console.error("Failed to record auto-approved imports:", autoQueueErr);
        }

        if (toEnqueue.length > 0) {
          const { error: bulkQueueErr } = await supabase
            .from("content_approval_queue")
            .insert(
              toEnqueue.map((e) => ({
                content_type: "episode" as const,
                content_id: e.id,
                user_id: e.user_id,
                status: "pending" as const,
              })),
            );
          if (bulkQueueErr) console.error("Failed to enqueue imported episodes:", bulkQueueErr);
        }
      }
    } catch (err) {
      console.error("Failed to process imported episodes after show approval:", err);
    }
  }


  // Notify the podcaster via email + in-app (fire-and-forget, mirrors rejectContent)
  try {
    const contentTitle = queueItem.content_type === "show"
      ? (await supabase.from("shows").select("title").eq("id", queueItem.content_id).single()).data?.title
      : (await supabase.from("episodes").select("title").eq("id", queueItem.content_id).single()).data?.title;

    await supabase.functions.invoke("notify-pending-approval", {
      body: {
        type: "content_approved",
        contentType: queueItem.content_type,
        contentTitle: contentTitle || "Untitled",
        podcasterUserId: queueItem.user_id,
        assignedPlatforms: platformsToAssign,
      },
    });
  } catch (err) {
    console.error("Failed to send approval notification:", err);
  }
}

/**
 * Network-admin approval path for shows.
 *
 * Marks the queue item as `pending_distribution` without assigning a hosting
 * platform, activating the show, or enqueueing a sync job. The Podgo Admin
 * later assigns the platform via the regular `approveContent` flow, which is
 * what triggers activation, sync, and the podcaster notification.
 *
 * Episodes do not use this path — episode approval inherits the parent show's
 * already-assigned hosting platform.
 */
export async function markShowPendingDistribution(
  queueId: string
): Promise<void> {
  const { data: sessionData } = await supabase.auth.getSession();
  if (!sessionData.session) throw new Error("Not authenticated");

  const { error } = await supabase
    .from("content_approval_queue")
    .update({
      status: "pending_distribution",
      reviewed_by: sessionData.session.user.id,
      reviewed_at: new Date().toISOString(),
    })
    .eq("id", queueId);

  if (error) throw error;
}

/**
 * Reject a queue item with reason
 */
export async function rejectContent(
  queueId: string,
  reason: string
): Promise<void> {
  const { data: sessionData } = await supabase.auth.getSession();
  if (!sessionData.session) throw new Error("Not authenticated");

  // Fetch queue item details before updating
  const { data: queueItem, error: fetchError } = await supabase
    .from("content_approval_queue")
    .select("content_type, content_id, user_id")
    .eq("id", queueId)
    .single();

  if (fetchError || !queueItem) throw fetchError || new Error("Queue item not found");

  const { error } = await supabase
    .from("content_approval_queue")
    .update({
      status: "rejected",
      rejection_reason: reason,
      reviewed_by: sessionData.session.user.id,
      reviewed_at: new Date().toISOString(),
    })
    .eq("id", queueId);

  if (error) throw error;

  // Deactivate the content and remove from external platforms
  if (queueItem.content_type === "show") {
    await supabase
      .from("shows")
      .update({ status: "paused" })
      .eq("id", queueItem.content_id);

    // Trigger platform removal (fire-and-forget)
    try {
      await supabase.functions.invoke(
        `podgo-automation/podcasts/${queueItem.content_id}`,
        { method: "DELETE" }
      );
    } catch (err) {
      console.error("Failed to remove rejected show from platforms:", err);
    }
  } else if (queueItem.content_type === "episode") {
    await supabase
      .from("episodes")
      .update({ status: "draft" })
      .eq("id", queueItem.content_id);

    // Trigger platform removal (fire-and-forget)
    try {
      await supabase.functions.invoke(
        `podgo-automation/episodes/${queueItem.content_id}`,
        { method: "DELETE" }
      );
    } catch (err) {
      console.error("Failed to remove rejected episode from platforms:", err);
    }
  }

  // Notify the podcaster via email
  try {
    const contentTitle = queueItem.content_type === "show"
      ? (await supabase.from("shows").select("title").eq("id", queueItem.content_id).single()).data?.title
      : (await supabase.from("episodes").select("title").eq("id", queueItem.content_id).single()).data?.title;

    await supabase.functions.invoke("notify-pending-approval", {
      body: {
        type: "content_rejected",
        contentType: queueItem.content_type,
        contentTitle: contentTitle || "Untitled",
        podcasterUserId: queueItem.user_id,
        rejectionReason: reason,
      },
    });
  } catch (err) {
    console.error("Failed to send rejection notification:", err);
  }
}

/**
/**
 * Get approval status for a specific content item (used by podcasters)
 */
export async function getApprovalStatus(
  contentType: "show" | "episode",
  contentId: string
): Promise<ApprovalQueueItem | null> {
  const { data, error } = await supabase
    .from("content_approval_queue")
    .select("*")
    .eq("content_type", contentType)
    .eq("content_id", contentId)
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error || !data) return null;
  return mapRow(data);
}

/**
 * Fetch real per-stage timestamps (latest queue row) for a content item.
 * Used by DeliveryTimelineCard to show truthful Pending/Approved times.
 */
export async function getApprovalTimestampsForContent(
  contentType: "show" | "episode",
  contentId: string,
): Promise<{ pendingAt: string | null; approvedAt: string | null }> {
  const { data, error } = await supabase
    .from("content_approval_queue")
    .select("created_at, reviewed_at")
    .eq("content_type", contentType)
    .eq("content_id", contentId)
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error || !data) return { pendingAt: null, approvedAt: null };
  return {
    pendingAt: data.created_at ?? null,
    approvedAt: data.reviewed_at ?? null,
  };
}

/**
 * Get a single approval queue item with full content details (admin review page)
 */
export async function getApprovalQueueItem(
  queueId: string
): Promise<{ queueItem: ApprovalQueueItem; content: any } | null> {
  const { data, error } = await supabase
    .from("content_approval_queue")
    .select("*")
    .eq("id", queueId)
    .single();

  if (error || !data) return null;

  const queueItem = mapRow(data);

  // Fetch the linked content
  let content: any = null;
  if (data.content_type === "show") {
    const { data: show } = await supabase
      .from("shows")
      .select("*")
      .eq("id", data.content_id)
      .single();
    content = show;
  } else {
    const { data: episode } = await supabase
      .from("episodes")
      .select("*, shows(id, title, cover_url, hosting_platform, contract_signed_at, contract_status, contract_required, docusign_envelope_id)")
      .eq("id", data.content_id)
      .single();
    content = episode;
  }

  // Fetch podcaster profile and reviewer profile in parallel
  const [profileResult, reviewerResult] = await Promise.all([
    supabase.from("profiles").select("display_name, avatar_url").eq("id", data.user_id).single(),
    data.reviewed_by
      ? supabase.from("profiles").select("display_name").eq("id", data.reviewed_by).single()
      : Promise.resolve({ data: null }),
  ]);

  queueItem.podcasterName = profileResult.data?.display_name || "Unknown User";
  queueItem.contentTitle = content?.title || "Unknown";
  if (data.reviewed_by) {
    queueItem.reviewerName = reviewerResult.data?.display_name || "Unknown Admin";
  }

  return { queueItem, content };
}

/**
 * Update ad markers on an episode (admin sets during review)
 */
export async function updateAdMarkers(
  episodeId: string,
  markers: any[]
): Promise<void> {
  const { error } = await supabase
    .from("episodes")
    .update({ ad_markers: markers as any })
    .eq("id", episodeId);

  if (error) throw error;
}

/**
 * Reset an approved/rejected item back to pending (admin only)
 */
export async function resetToPending(queueId: string): Promise<void> {
  const { error } = await supabase
    .from("content_approval_queue")
    .update({
      status: "pending",
      reviewed_by: null,
      reviewed_at: null,
      rejection_reason: null,
      assigned_platforms: [],
    })
    .eq("id", queueId);

  if (error) throw error;
}

/**
 * Delete a queue item (admin only)
 */
export async function deleteQueueItem(queueId: string): Promise<void> {
  const { error } = await supabase
    .from("content_approval_queue")
    .delete()
    .eq("id", queueId);

  if (error) throw error;
}

/**
 * Notify admins about new content pending approval.
 */
export async function notifyAdmins(
  contentType: "show" | "episode",
  contentTitle: string
): Promise<void> {
  try {
    // Resolve podcaster display name from the current user's profile so the
    // admin email shows a real name instead of "Unknown". Mirrors the
    // resolution order used by the edge function's resolveUserName helper.
    let podcasterName: string | undefined;
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: profile } = await supabase
          .from("profiles")
          .select("display_name, first_name, last_name, podcast_show_name")
          .eq("id", user.id)
          .maybeSingle();
        const fullName = [profile?.first_name, profile?.last_name]
          .filter(Boolean)
          .join(" ")
          .trim();
        podcasterName =
          profile?.display_name?.trim() ||
          fullName ||
          profile?.podcast_show_name?.trim() ||
          user.email?.split("@")[0] ||
          undefined;
      }
    } catch (lookupErr) {
      console.warn("notifyAdmins: failed to resolve podcaster name", lookupErr);
    }

    await supabase.functions.invoke("notify-pending-approval", {
      body: {
        type: "content_pending",
        contentType,
        contentTitle,
        podcasterName,
        submittedAt: new Date().toISOString(),
      },
    });
  } catch (err) {
    console.error("Failed to notify admins:", err);
  }
}
